import SearchRounded from "@mui/icons-material/SearchRounded";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Proposal } from "../../../../types/proposal.types";
import ProposalCard from "./ProposalCard";
import { useState } from "react";
import { tokens } from "../../../../../../../theme";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

interface ProposalListProps {
  proposals: Proposal[];
  onCreateProposal?: () => void;
  onViewProposal?: (id: number) => void;
  loading?: boolean;
  totalCount?: number;
  nextUrl?: string | null;
  prevUrl?: string | null;
  onNext?: () => void;
  onPrev?: () => void;
}

export default function ProposalList({
  proposals,
  onCreateProposal,
  onViewProposal,
  loading = false,
  totalCount,
  nextUrl,
  prevUrl,
  onNext,
  onPrev,
}: ProposalListProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [search, setSearch] = useState("");

  const filtered = proposals.filter((p) => {
    const title = (p.title ?? "").toLowerCase();
    const status = (p.status ?? "").toLowerCase();
    const contact =
      `${p.booking?.contact?.first_name ?? ""} ${p.booking?.contact?.last_name ?? ""}`.toLowerCase();
    const q = search.toLowerCase();
    return title.includes(q) || status.includes(q) || contact.includes(q);
  });

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: colors.primary[400] }} />
        </Box>
      );
    }

    if (proposals.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "rgba(114,78,65,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 30, color: colors.primary[400] }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "text.primary" }}>
            No proposals yet
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "text.secondary", textAlign: "center", maxWidth: 260 }}>
            Create your first proposal to get started
          </Typography>
        </Box>
      );
    }

    if (filtered.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 1,
          }}
        >
          <SearchRounded sx={{ fontSize: 36, color: "text.disabled" }} />
          <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "text.secondary" }}>
            No results for &quot;{search}&quot;
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "text.disabled" }}>
            Try a different name, status, or title
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={1.5}>
        {filtered.map((proposal) => (
          <ProposalCard
            key={proposal.id}
            proposal={proposal}
            onView={onViewProposal}
          />
        ))}
      </Stack>
    );
  };

  return (
    <Paper sx={{ flex: 1, borderRadius: "14px", p: { xs: 1.5, sm: 2 }, boxShadow: 0 }}>
      {/* Header row */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        sx={{ mb: 2 }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        gap={1}
      >
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search for Proposals"
          size="small"
          sx={{
            width: { xs: "100%", sm: 360 },
            maxWidth: "100%",
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              bgcolor: "background.paper",
              fontSize: "12px",
              height: 38,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchRounded sx={{ color: "text.secondary", fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          onClick={onCreateProposal}
          sx={{
            bgcolor: colors.primary[400],
            color: "white",
            borderRadius: "10px",
            px: 2.5,
            height: 38,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "12px",
            whiteSpace: "nowrap",
            flexShrink: 0,
            "&:hover": { bgcolor: colors.primary[500] },
          }}
        >
          Create New Proposal
        </Button>
      </Stack>

      {/* Cards */}
      <Box
        sx={{
          maxHeight: 520,
          overflowY: "auto",
          pr: 0.5,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
          "&::-webkit-scrollbar-thumb": { bgcolor: "action.hover", borderRadius: 4 },
        }}
      >
        {renderContent()}
      </Box>

      {/* Pagination */}
      {proposals.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 1,
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "12px" }}>
            {totalCount} total
          </Typography>
          <IconButton disabled={!prevUrl} onClick={onPrev} size="small">
            <ChevronLeft />
          </IconButton>
          <IconButton disabled={!nextUrl} onClick={onNext} size="small">
            <ChevronRight />
          </IconButton>
        </Box>
      )}
    </Paper>
  );
}